import { useQuery } from "@tanstack/react-query";
import type { BoxState, Read } from "@/components/ui/box.schema";
import { historyBox, historyRead } from "@/components/history/history-boxes";
import { importsApi, type TermCoverage } from "@/lib/api/imports";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { registerText as text, registerWords as words } from "./register-copy";

/** Closed terms, newest first by their end, then start; a term without dates keeps the server's order after them. */
export function closedTermsNewestFirst(terms: TermCoverage[]): TermCoverage[] {
  const at = (t: TermCoverage) => t.end_date ?? t.start_date ?? "";
  return terms.filter((t) => t.status === "closed").map((t, i) => [t, i] as const)
    .sort(([a, i], [b, j]) => (at(a) && at(b) ? at(b).localeCompare(at(a)) : at(a) ? -1 : at(b) ? 1 : 0) || i - j).map(([t]) => t);
}

/**
 * A term is Complete only when it has sections, every section has an
 * instructor and an enrollment, and every enrollment has a certified outcome.
 * Otherwise it names its largest gap; a tie goes to the gap met first in the
 * order a records file builds a term: instructors, enrollments, outcomes.
 */
export function termState(t: TermCoverage): BoxState {
  if (!t.sections) return { class: "waiting", word: words.noSectionsRecorded };
  const gaps = [["instructor", t.sections_without_instructor], ["enrollment", t.sections_without_enrollment], ["outcome", t.enrollments_without_outcome]] as const;
  const [kind, n] = gaps.reduce((largest, gap) => gap[1] > largest[1] ? gap : largest);
  return n > 0 ? { class: "waiting", word: text.gap(kind, n) } : { class: "done", word: words.complete };
}

const complete = (t: TermCoverage) => termState(t).class === "done";

/** Term coverage: the visible finish line of a historical migration, one closed term per row. */
export function useTermCoverageBox() {
  // Under "import-batches", so an applied run reads coverage again.
  const query = useQuery({ queryKey: ["import-batches", "coverage"], queryFn: () => importsApi.getCoverage() });
  const base = historyRead(query);
  const read: Read<TermCoverage[]> = base.status === "ready"
    ? (() => { const terms = closedTermsNewestFirst(base.value.terms); return terms.length ? { ...base, value: terms } : { status: "empty", sentence: words.noClosedTerms, sub: words.noClosedTermsSub }; })()
    : base.status === "stale" ? { ...base, last: closedTermsNewestFirst(base.last.terms) } : base;
  return historyBox({ kind: "list", id: "history-term-coverage", job: words.coverageJob, label: words.coverage,
    owner: { system: "imam" }, read, fold: "choice", act: { does: "open", verb: words.readCoverage },
    detail: (v: TermCoverage[]) => text.coverageDetail(v.filter(complete).length, v.length),
    state: (v: TermCoverage[]) => {
      const open = v.filter((t) => !complete(t)).length;
      return open ? { class: "waiting", word: text.termsIncomplete(open) } : { class: "done", word: words.everyTermComplete };
    },
    figures: (v: TermCoverage[]) => {
      const missing = v.reduce((n, t) => n + t.enrollments_without_outcome, 0);
      return [{ value: v.filter(complete).length, unit: text.termsComplete(v.length) }, { value: missing, unit: text.withoutOutcome(missing) }];
    },
    rows: (v: TermCoverage[]) => v,
    row: { key: (t) => t.term_id, title: (t) => t.name,
      // A term with no sections has nothing to read in Course History.
      nav: (t) => t.sections ? `${PROTECTED_ROUTES.admin.courseHistory}?${new URLSearchParams({ term_id: t.term_id })}` : undefined,
      state: termState, facts: (t) => text.termFacts(t) },
  });
}
